import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import '../PartnerReferalRewardModal/PartnerReferalRewardModal.css';
import './PurchaseInterestRewardModal.css';
import PageButton from '../../elements/PageButton/PageButton';
import {API_BASE} from '../../../utils/apiConfig';
import {applyPurchaseInterestRewardClaim} from '../../../redux/actions';
import {getPurchaseInterestReward} from '../../../configs/purchaseInterestRewards';
import {MENU_SOUND_MANAGER} from '../../../scenes/MenuSoundManager';
import {trackAdGoal, AD_GOALS} from '../../../analytics/adTracking';

const PAGE_ID = 'PurchaseInterestRewardModal';

/**
 * Модалка попытки покупки премиума/кристаллов: покупка пока не подключена, но
 * за проявленный интерес игрок один раз получает подарок
 * (см. configs/purchaseInterestRewards.js). Стиль — как у модалки реферальной
 * награды, ее же классы.
 *
 * Получал ли игрок подарок, известно из auth (receivedPurchaseInterestRewards),
 * поэтому лишнего запроса на открытии нет. Признак фиксируется на монтировании:
 * после клейма текст модалки не должен переключаться на «уже получен» под
 * рукой у игрока.
 */
function PurchaseInterestRewardModal({type, onClose}) {
  const dispatch = useDispatch();
  const receivedTypes = useSelector((state) => state.private.auth.receivedPurchaseInterestRewards);
  const reward = getPurchaseInterestReward(type);

  const [visible, setVisible] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [isClaimed, setIsClaimed] = useState(false);
  const [error, setError] = useState(null);
  const wasReceivedBeforeRef = useRef((receivedTypes || []).includes(type));

  useEffect(() => {
    // Попытка покупки — рекламная цель: покупка еще не подключена, но интерес
    // к ней уже сигнал для рекламных кампаний (см. analytics/adTracking.js)
    trackAdGoal(AD_GOALS.PURCHASE_INTENT, {params: {type}});
    const showTimer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(showTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose?.(), 300);
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && !claiming) {
      handleClose();
    }
  };

  const handleClaimClick = async () => {
    if (claiming) return;
    setClaiming(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/purchase-interest-reward`, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({type}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Не удалось получить награду');
      }
      dispatch(applyPurchaseInterestRewardClaim({
        type,
        numens: data.numens,
        crystals: data.crystals,
        premiumExpiresAt: data.premiumExpiresAt,
      }));
      setIsClaimed(true);
      MENU_SOUND_MANAGER.playChestOpen();
      handleClose();
    } catch (e) {
      console.error('Claim purchase interest reward error', e);
      setError(e.message || 'Не удалось получить награду');
    } finally {
      setClaiming(false);
    }
  };

  if (!reward) return null;

  const showReward = !wasReceivedBeforeRef.current && !isClaimed;

  return (
    <div
      className={`partner-referal-reward-overlay ${visible ? 'partner-referal-reward-overlay_visible' : ''}`}
      onClick={handleOverlayClick}
      role={'dialog'}
      aria-modal={'true'}
    >
      <div className={`partner-referal-reward-modal ${visible ? 'partner-referal-reward-modal_visible' : ''}`}>
        <h2 className={'partner-referal-reward-title'}>Спасибо за интерес!</h2>

        <p className={'partner-referal-reward-description'}>
          Покупка станет доступна в ближайшее время.
          {showReward && (
            <>
              <br/>
              А пока — примите подарок за проявленный интерес:
            </>
          )}
        </p>

        {showReward && (
          <div className={'partner-referal-reward-list'}>
            <div className={'partner-referal-reward-item purchase-interest-reward-item'}>
              <img className={'partner-referal-reward-item-icon'} src={reward.iconSrc} alt={''}/>
              <span className={'partner-referal-reward-item-label'}>{reward.label}</span>
            </div>
          </div>
        )}

        {error && <p className={'partner-referal-reward-error'}>{error}</p>}

        <div className={'main-menu-buttons partner-referal-reward-buttons'}>
          {showReward ? (
            <PageButton
              onClick={handleClaimClick}
              pageId={PAGE_ID}
              buttonId={`claim_${type}`}
              disabled={claiming}
            >
              {claiming ? 'Получение...' : 'Забрать дар'}
            </PageButton>
          ) : (
            <PageButton onClick={handleClose} pageId={PAGE_ID} buttonId={`close_${type}`}>
              Понятно
            </PageButton>
          )}
        </div>
      </div>
    </div>
  );
}

export default PurchaseInterestRewardModal;
